import { supabase } from '../lib/supabase';

// Normalize error to always be a proper Error instance
function normalizeError(err, fallback) {
  if (err instanceof Error) return err;
  if (err && typeof err === 'object') {
    const msg = err?.message || fallback || 'Unknown error';
    return Object.assign(new Error(msg), err);
  }
  return new Error(err || fallback || 'Unknown error');
}

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(item => toCamelCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      acc[camelKey] = toCamelCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(item => toSnakeCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
      acc[snakeKey] = toSnakeCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

export const projectService = {
  // Get all projects with phases
  async getAll() {
    try {
      const { data, error } = await supabase?.from('projects')?.select(`
          *,
          phases:project_phases(*),
          assigned_pm:user_profiles!projects_assigned_pm_id_fkey(id, full_name, first_name, last_name, nickname, email, avatar_url)
        `)?.eq('is_archived', false)?.neq('status', 'Cancelled')?.order('created_at', { ascending: false })?.limit(1000);

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching projects:', error?.message);
      return { data: null, error };
    }
  },

  // Get all archived projects
  async getArchived() {
    try {
      const { data, error } = await supabase?.from('projects')?.select(`
          *,
          phases:project_phases(*),
          assigned_pm:user_profiles!projects_assigned_pm_id_fkey(id, full_name, first_name, last_name, nickname, email, avatar_url)
        `)?.or('is_archived.eq.true,status.eq.Cancelled')?.order('updated_at', { ascending: false })?.limit(1000);

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching archived projects:', error?.message);
      return { data: null, error };
    }
  },

  // Archive a project
  async archive(id, currentStatus) {
    try {
      const { data, error } = await supabase?.from('projects')?.update({
        is_archived: true,
        pre_archive_status: currentStatus
      })?.eq('id', id)?.select()?.single();

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error archiving project:', error?.message);
      return { data: null, error };
    }
  },

  // Cancel a project — sets status to Cancelled, archives it, and preserves pre-cancel status
  async cancel(id, currentStatus) {
    try {
      const { data, error } = await supabase?.from('projects')?.update({
        is_archived: true,
        status: 'Cancelled',
        pre_archive_status: currentStatus
      })?.eq('id', id)?.select()?.single();

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error cancelling project:', error?.message);
      return { data: null, error };
    }
  },

  // Unarchive a project
  async unarchive(id, preArchiveStatus) {
    try {
      const { data, error } = await supabase?.from('projects')?.update({
        is_archived: false,
        pre_archive_status: null
      })?.eq('id', id)?.select()?.single();

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error unarchiving project:', error?.message);
      return { data: null, error };
    }
  },

  // Restore a cancelled project back to Active (In Progress) status
  async restore(id) {
    try {
      const { data, error } = await supabase?.from('projects')?.update({
        is_archived: false,
        status: 'In Progress',
        pre_archive_status: null
      })?.eq('id', id)?.select()?.single();

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error restoring project:', error?.message);
      return { data: null, error };
    }
  },

  // Get single project by ID
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('projects')?.select(`
          *,
          phases:project_phases(*),
          assigned_pm:user_profiles!projects_assigned_pm_id_fkey(id, full_name, first_name, last_name, nickname, email, avatar_url)
        `)?.eq('id', id)?.single();

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching project:', error?.message);
      return { data: null, error };
    }
  },

  // Create new project
  async create(projectData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const snakeData = toSnakeCase({ ...projectData, createdBy: user?.id });
      
      // If converting from bid, also update the bid status and add conversion date
      if (projectData?.convertedFromBid && projectData?.bidNumber) {
        const conversionDate = new Date()?.toISOString()?.split('T')?.[0];
        snakeData.conversion_date = conversionDate;
      }
      
      const { data, error } = await supabase?.from('projects')?.insert(snakeData)?.select()?.single();

      if (error) throw normalizeError(error);
      
      // Update the bid status to "Converted to a Project" if this project was converted from a bid
      if (projectData?.convertedFromBid && projectData?.bidNumber) {
        const { error: bidUpdateError } = await supabase
          ?.from('bids')
          ?.update({ status: 'Converted to a Project' })
          ?.eq('bid_number', projectData?.bidNumber);
        
        if (bidUpdateError) {
          console.error('Error updating bid status:', bidUpdateError?.message);
          // Don't throw error here - project was created successfully
        }
      }
      
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error creating project:', error?.message);
      return { data: null, error };
    }
  },

  // Update project
  async update(id, projectData) {
    try {
      const snakeData = toSnakeCase(projectData);
      const { data, error } = await supabase?.from('projects')?.update(snakeData)?.eq('id', id)?.select()?.single();

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error updating project:', error?.message);
      return { data: null, error };
    }
  },

  // Delete project
  async delete(id) {
    try {
      const { error } = await supabase?.from('projects')?.delete()?.eq('id', id);

      if (error) throw normalizeError(error);
      return { error: null };
    } catch (error) {
      console.error('Error deleting project:', error?.message);
      return { error };
    }
  },

  // Get project phases
  async getPhases(projectId) {
    try {
      const { data, error } = await supabase?.from('project_phases')?.select('*')?.eq('project_id', projectId)?.order('start_date', { ascending: true });

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching phases:', error?.message);
      return { data: null, error };
    }
  },

  // Create phase
  async createPhase(phaseData) {
    try {
      const snakeData = toSnakeCase(phaseData);
      const { data, error } = await supabase?.from('project_phases')?.insert(snakeData)?.select()?.single();

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error creating phase:', error?.message);
      return { data: null, error };
    }
  },

  // Update phase
  async updatePhase(id, phaseData) {
    try {
      const snakeData = toSnakeCase(phaseData);
      const { data, error } = await supabase?.from('project_phases')?.update(snakeData)?.eq('id', id)?.select()?.single();

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error updating phase:', error?.message);
      return { data: null, error };
    }
  },

  // Delete phase
  async deletePhase(id) {
    try {
      const { error } = await supabase?.from('project_phases')?.delete()?.eq('id', id);

      if (error) throw normalizeError(error);
      return { error: null };
    } catch (error) {
      console.error('Error deleting phase:', error?.message);
      return { error };
    }
  },

  // ===== DEFAULT PHASES MANAGEMENT =====

  // Get all default phases
  async getDefaultPhases() {
    try {
      const { data, error } = await supabase
        ?.from('default_phases')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('sort_order', { ascending: true });
      if (error) throw normalizeError(error);
      return { data: toCamelCase(data || []), error: null };
    } catch (error) {
      console.error('Error fetching default phases:', error?.message);
      return { data: [], error };
    }
  },

  // Get ALL default phases (including inactive) for admin
  async getAllDefaultPhases() {
    try {
      const { data, error } = await supabase
        ?.from('default_phases')
        ?.select('*')
        ?.order('sort_order', { ascending: true });
      if (error) throw normalizeError(error);
      return { data: toCamelCase(data || []), error: null };
    } catch (error) {
      console.error('Error fetching all default phases:', error?.message);
      return { data: [], error };
    }
  },

  // Create default phase
  async createDefaultPhase(phaseData) {
    try {
      const snakeData = toSnakeCase(phaseData);
      const { data, error } = await supabase
        ?.from('default_phases')
        ?.insert(snakeData)
        ?.select()
        ?.single();
      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error creating default phase:', error?.message);
      return { data: null, error };
    }
  },

  // Update default phase
  async updateDefaultPhase(id, phaseData) {
    try {
      const snakeData = toSnakeCase(phaseData);
      const { data, error } = await supabase
        ?.from('default_phases')
        ?.update(snakeData)
        ?.eq('id', id)
        ?.select()
        ?.single();
      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error updating default phase:', error?.message);
      return { data: null, error };
    }
  },

  // Delete default phase
  async deleteDefaultPhase(id) {
    try {
      const { error } = await supabase
        ?.from('default_phases')
        ?.delete()
        ?.eq('id', id);
      if (error) throw normalizeError(error);
      return { error: null };
    } catch (error) {
      console.error('Error deleting default phase:', error?.message);
      return { error };
    }
  },

  // Reorder default phases
  async reorderDefaultPhases(orderedIds) {
    try {
      const updates = orderedIds?.map((id, index) =>
        supabase?.from('default_phases')?.update({ sort_order: index + 1 })?.eq('id', id)
      );
      await Promise.all(updates);
      return { error: null };
    } catch (error) {
      console.error('Error reordering default phases:', error?.message);
      return { error };
    }
  },

  // Apply default phases to a project
  async applyDefaultPhasesToProject(projectId, project) {
    try {
      const { data: defaults } = await this.getDefaultPhases();
      if (!defaults?.length) return { error: null };
      const phases = defaults?.map((dp, index) => ({
        project_id: projectId,
        name: dp?.name,
        description: dp?.description || '',
        status: 'Planning',
        progress: 0,
        start_date: project?.startDate || new Date()?.toISOString()?.split('T')?.[0],
        end_date: project?.endDate || new Date()?.toISOString()?.split('T')?.[0],
        deliverable_count: 0,
        sort_order: index + 1
      }));
      const { error } = await supabase?.from('project_phases')?.insert(phases);
      if (error) throw normalizeError(error);
      return { error: null };
    } catch (error) {
      console.error('Error applying default phases:', error?.message);
      return { error };
    }
  },

  // ===== DELIVERABLES MANAGEMENT =====

  // Get all deliverables for a project with dependencies and resources
  async getDeliverables(projectId) {
    try {
      const { data, error } = await supabase
        ?.from('deliverables')
        ?.select(`
          *,
          dependencies:deliverable_dependencies!deliverable_dependencies_deliverable_id_fkey(
            depends_on_id
          ),
          resources:deliverable_resource_assignments(
            start_date,
            end_date,
            resource:resources!deliverable_resource_assignments_resource_id_fkey(*)
          )
        `)
        ?.eq('project_id', projectId)
        ?.order('start_date', { ascending: true });

      if (error) throw normalizeError(error);

      // Transform data to include flat arrays
      const transformed = (data || [])?.map(deliverable => ({
        ...toCamelCase(deliverable),
        dependsOn: deliverable?.dependencies?.map(d => d?.depends_on_id) || [],
        assignedResources: deliverable?.resources?.map(r => ({
          ...toCamelCase(r?.resource),
          allocationStartDate: r?.start_date || null,
          allocationEndDate: r?.end_date || null
        })) || []
      }));

      return { data: transformed, error: null };
    } catch (error) {
      console.error('Error fetching project deliverables:', error?.message);
      return { data: null, error };
    }
  },

  // Create deliverable for a project
  async createDeliverable(deliverableData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const { dependsOn, assignedResources, resourceDates, ...coreData } = deliverableData;
      const snakeData = toSnakeCase({ ...coreData, createdBy: user?.id });

      // Insert deliverable
      const { data, error } = await supabase
        ?.from('deliverables')
        ?.insert(snakeData)
        ?.select()
        ?.single();

      if (error) throw normalizeError(error);

      // Insert dependencies if provided
      if (dependsOn && dependsOn?.length > 0) {
        const dependencies = dependsOn?.map(dependsOnId => ({
          deliverable_id: data?.id,
          depends_on_id: dependsOnId
        }));

        const { error: depError } = await supabase
          ?.from('deliverable_dependencies')
          ?.insert(dependencies);

        if (depError) throw normalizeError(depError);
      }

      // Insert resource assignments if provided
      if (assignedResources && assignedResources?.length > 0) {
        const assignments = assignedResources?.map(resourceId => ({
          deliverable_id: data?.id,
          resource_id: resourceId
        }));

        const { error: resError } = await supabase
          ?.from('deliverable_resource_assignments')
          ?.insert(assignments);

        if (resError) throw normalizeError(resError);
      }

      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error creating deliverable:', error?.message);
      return { data: null, error };
    }
  },

  // Update deliverable
  async updateDeliverable(deliverableId, deliverableData) {
    try {
      const { dependsOn, assignedResources, resourceDates, ...coreData } = deliverableData;
      const snakeData = toSnakeCase(coreData);

      // Update deliverable
      const { data, error } = await supabase
        ?.from('deliverables')
        ?.update(snakeData)
        ?.eq('id', deliverableId)
        ?.select();

      if (error) throw normalizeError(error);

      const updatedRow = Array.isArray(data) ? data?.[0] : data;

      // Update dependencies if provided
      if (dependsOn !== undefined) {
        // Delete existing dependencies
        await supabase
          ?.from('deliverable_dependencies')
          ?.delete()
          ?.eq('deliverable_id', deliverableId);

        // Insert new dependencies
        if (dependsOn?.length > 0) {
          const dependencies = dependsOn?.map(dependsOnId => ({
            deliverable_id: deliverableId,
            depends_on_id: dependsOnId
          }));

          const { error: depError } = await supabase
            ?.from('deliverable_dependencies')
            ?.insert(dependencies);

          if (depError) throw normalizeError(depError);
        }
      }

      // Update resource assignments if provided
      if (assignedResources !== undefined) {
        // Delete existing assignments
        await supabase
          ?.from('deliverable_resource_assignments')
          ?.delete()
          ?.eq('deliverable_id', deliverableId);

        // Insert new assignments
        if (assignedResources?.length > 0) {
          const assignments = assignedResources?.map(resourceId => ({
            deliverable_id: deliverableId,
            resource_id: resourceId,
            start_date: resourceDates?.[resourceId]?.startDate || null,
            end_date: resourceDates?.[resourceId]?.endDate || null
          }));

          const { error: resError } = await supabase
            ?.from('deliverable_resource_assignments')
            ?.insert(assignments);

          if (resError) throw normalizeError(resError);
        }
      }

      return { data: toCamelCase(updatedRow), error: null };
    } catch (error) {
      console.error('Error updating deliverable:', error?.message);
      return { data: null, error };
    }
  },

  // Delete deliverable
  async deleteDeliverable(deliverableId) {
    try {
      const { error } = await supabase
        ?.from('deliverables')
        ?.delete()
        ?.eq('id', deliverableId);

      if (error) throw normalizeError(error);
      return { error: null };
    } catch (error) {
      console.error('Error deleting deliverable:', error?.message);
      return { error };
    }
  },

  // ===== DEPENDENCY MANAGEMENT =====

  // Add dependency between deliverables
  async addDependency(deliverableId, dependsOnId) {
    try {
      const { data, error } = await supabase
        ?.from('deliverable_dependencies')
        ?.insert({
          deliverable_id: deliverableId,
          depends_on_id: dependsOnId
        })
        ?.select()
        ?.single();

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error adding dependency:', error?.message);
      return { data: null, error };
    }
  },

  // Remove dependency
  async removeDependency(deliverableId, dependsOnId) {
    try {
      const { error } = await supabase
        ?.from('deliverable_dependencies')
        ?.delete()
        ?.eq('deliverable_id', deliverableId)
        ?.eq('depends_on_id', dependsOnId);

      if (error) throw normalizeError(error);
      return { error: null };
    } catch (error) {
      console.error('Error removing dependency:', error?.message);
      return { error };
    }
  },

  // Get all dependencies for a deliverable
  async getDependencies(deliverableId) {
    try {
      const { data, error } = await supabase
        ?.from('deliverable_dependencies')
        ?.select(`
          *,
          depends_on:deliverables!deliverable_dependencies_depends_on_id_fkey(*)
        `)
        ?.eq('deliverable_id', deliverableId);

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching dependencies:', error?.message);
      return { data: null, error };
    }
  },

  // ===== RESOURCE ASSIGNMENT TRACKING =====

  // Assign resource to deliverable
  async assignResource(deliverableId, resourceId) {
    try {
      const { data, error } = await supabase
        ?.from('deliverable_resource_assignments')
        ?.insert({
          deliverable_id: deliverableId,
          resource_id: resourceId
        })
        ?.select()
        ?.single();

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error assigning resource:', error?.message);
      return { data: null, error };
    }
  },

  // Unassign resource from deliverable
  async unassignResource(deliverableId, resourceId) {
    try {
      const { error } = await supabase
        ?.from('deliverable_resource_assignments')
        ?.delete()
        ?.eq('deliverable_id', deliverableId)
        ?.eq('resource_id', resourceId);

      if (error) throw normalizeError(error);
      return { error: null };
    } catch (error) {
      console.error('Error unassigning resource:', error?.message);
      return { error };
    }
  },

  // Get all resource assignments for a project
  async getResourceAssignments(projectId) {
    try {
      const { data, error } = await supabase
        ?.from('deliverable_resource_assignments')
        ?.select(`
          *,
          deliverable:deliverables!inner(
            id,
            name,
            start_date,
            end_date,
            status,
            project_id
          ),
          resource:resources(*)
        `)
        ?.eq('deliverable.project_id', projectId);

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching resource assignments:', error?.message);
      return { data: null, error };
    }
  },

  // Get resource assignments for a specific deliverable
  async getDeliverableResources(deliverableId) {
    try {
      const { data, error } = await supabase
        ?.from('deliverable_resource_assignments')
        ?.select(`
          *,
          resource:resources(*)
        `)
        ?.eq('deliverable_id', deliverableId);

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching deliverable resources:', error?.message);
      return { data: null, error };
    }
  },

  // Check for resource conflicts within a project
  async checkResourceConflicts(projectId, resourceId, startDate, endDate, excludeDeliverableId = null) {
    try {
      const { data, error } = await supabase
        ?.from('deliverable_resource_assignments')
        ?.select(`
          deliverable:deliverables!inner(
            id,
            name,
            start_date,
            end_date,
            project_id
          )
        `)
        ?.eq('resource_id', resourceId)
        ?.eq('deliverable.project_id', projectId);

      if (error) throw normalizeError(error);

      // Filter for date overlaps
      const conflicts = (data || [])?.filter(assignment => {
        const deliverable = assignment?.deliverable;
        if (!deliverable || deliverable?.id === excludeDeliverableId) return false;

        const assignmentStart = new Date(deliverable?.start_date);
        const assignmentEnd = new Date(deliverable?.end_date);
        const checkStart = new Date(startDate);
        const checkEnd = new Date(endDate);

        // Check for overlap
        return assignmentStart <= checkEnd && assignmentEnd >= checkStart;
      })?.map(assignment => toCamelCase(assignment?.deliverable));

      return { data: conflicts, error: null };
    } catch (error) {
      console.error('Error checking resource conflicts:', error?.message);
      return { data: null, error };
    }
  },

  // Get project with full details (deliverables, dependencies, resources)
  async getProjectWithDetails(projectId) {
    try {
      // Fetch project
      const { data: project, error: projectError } = await supabase
        ?.from('projects')
        ?.select('*')
        ?.eq('id', projectId)
        ?.single();

      if (projectError) throw normalizeError(projectError);

      // Fetch deliverables with dependencies and resources
      const { data: deliverables, error: deliverablesError } = await this.getDeliverables(projectId);
      if (deliverablesError) throw normalizeError(deliverablesError);

      // Fetch resource assignments
      const { data: resourceAssignments, error: assignmentsError } = await this.getResourceAssignments(projectId);
      if (assignmentsError) throw normalizeError(assignmentsError);

      return {
        data: {
          ...toCamelCase(project),
          deliverables: deliverables || [],
          resourceAssignments: resourceAssignments || []
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching project with details:', error?.message);
      return { data: null, error };
    }
  },

  // Get users with PM flag enabled
  async getPMUsers() {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, first_name, last_name, nickname, email, avatar_url')
        ?.eq('is_pm', true)
        ?.eq('is_active', true)
        ?.order('full_name', { ascending: true });

      if (error) throw normalizeError(error);
      return { data: toCamelCase(data || []), error: null };
    } catch (error) {
      console.error('Error fetching PM users:', error?.message);
      return { data: [], error };
    }
  },

  // Subscribe to real-time project changes
  subscribeToChanges(onInsert, onUpdate, onDelete) {
    const channel = supabase?.channel('projects_realtime')
      ?.on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'projects' },
        (payload) => onInsert && onInsert(toCamelCase(payload?.new))
      )
      ?.on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'projects' },
        (payload) => onUpdate && onUpdate(toCamelCase(payload?.new))
      )
      ?.on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'projects' },
        (payload) => onDelete && onDelete(payload?.old?.id)
      )
      ?.subscribe();

    return channel;
  },

  unsubscribe(channel) {
    if (channel) supabase?.removeChannel(channel);
  },

  // Bulk complete all children of a phase (deliverables, tasks, subtasks)
  async bulkCompletePhaseChildren(projectId, phaseId) {
    try {
      // Complete all deliverables in the phase
      const { error: delError } = await supabase
        ?.from('deliverables')
        ?.update({ status: 'completed' })
        ?.eq('project_id', projectId)
        ?.eq('phase_id', phaseId);
      if (delError) throw normalizeError(delError);

      // Get all deliverable IDs in this phase to find tasks/subtasks
      const { data: phaseDeliverables, error: fetchError } = await supabase
        ?.from('deliverables')
        ?.select('id')
        ?.eq('project_id', projectId)
        ?.eq('phase_id', phaseId)
        ?.in('row_type', ['deliverable', null]);
      if (fetchError) throw normalizeError(fetchError);

      const deliverableIds = (phaseDeliverables || [])?.map(d => d?.id);

      if (deliverableIds?.length > 0) {
        // Complete all tasks under those deliverables
        const { data: tasks, error: taskFetchError } = await supabase
          ?.from('deliverables')
          ?.select('id')
          ?.eq('project_id', projectId)
          ?.eq('row_type', 'task')
          ?.in('parent_id', deliverableIds);
        if (taskFetchError) throw normalizeError(taskFetchError);

        const taskIds = (tasks || [])?.map(t => t?.id);

        const { error: taskError } = await supabase
          ?.from('deliverables')
          ?.update({ status: 'completed' })
          ?.eq('project_id', projectId)
          ?.eq('row_type', 'task')
          ?.in('parent_id', deliverableIds);
        if (taskError) throw normalizeError(taskError);

        if (taskIds?.length > 0) {
          // Complete all subtasks under those tasks
          const { error: subtaskError } = await supabase
            ?.from('deliverables')
            ?.update({ status: 'completed' })
            ?.eq('project_id', projectId)
            ?.eq('row_type', 'subtask')
            ?.in('parent_id', taskIds);
          if (subtaskError) throw normalizeError(subtaskError);
        }
      }

      return { error: null };
    } catch (error) {
      console.error('Error bulk completing phase children:', error?.message);
      return { error };
    }
  },

  // Bulk complete all children of a deliverable (tasks and subtasks)
  async bulkCompleteDeliverableChildren(projectId, deliverableId) {
    try {
      // Complete all tasks under this deliverable
      const { data: tasks, error: taskFetchError } = await supabase
        ?.from('deliverables')
        ?.select('id')
        ?.eq('project_id', projectId)
        ?.eq('row_type', 'task')
        ?.eq('parent_id', deliverableId);
      if (taskFetchError) throw normalizeError(taskFetchError);

      const taskIds = (tasks || [])?.map(t => t?.id);

      const { error: taskError } = await supabase
        ?.from('deliverables')
        ?.update({ status: 'completed' })
        ?.eq('project_id', projectId)
        ?.eq('row_type', 'task')
        ?.eq('parent_id', deliverableId);
      if (taskError) throw normalizeError(taskError);

      if (taskIds?.length > 0) {
        // Complete all subtasks under those tasks
        const { error: subtaskError } = await supabase
          ?.from('deliverables')
          ?.update({ status: 'completed' })
          ?.eq('project_id', projectId)
          ?.eq('row_type', 'subtask')
          ?.in('parent_id', taskIds);
        if (subtaskError) throw normalizeError(subtaskError);
      }

      return { error: null };
    } catch (error) {
      console.error('Error bulk completing deliverable children:', error?.message);
      return { error };
    }
  },

  // Bulk complete all subtasks of a task
  async bulkCompleteTaskChildren(projectId, taskId) {
    try {
      const { error } = await supabase
        ?.from('deliverables')
        ?.update({ status: 'completed' })
        ?.eq('project_id', projectId)
        ?.eq('row_type', 'subtask')
        ?.eq('parent_id', taskId);
      if (error) throw normalizeError(error);
      return { error: null };
    } catch (error) {
      console.error('Error bulk completing task children:', error?.message);
      return { error };
    }
  },
};